import { SLA_DAYS } from '@/lib/dd-queue/constants'
import { isOverdue } from '@/lib/dd-queue/dueAt'

export function ageClass(age: number): string {
  if (age > SLA_DAYS) return 'red'
  if (age > 2) return 'warn'
  return ''
}

/** DD-DUE-24H-002 — overdue from due_at (INTENTIONAL vs age > SLA). */
export function dueClass(dueAtIso: string | undefined | null): string {
  return isOverdue(dueAtIso) ? 'due-past' : ''
}

/** Reference helpers.ts — USD currency for PMA change panels and attribute pre-fill. */
export function fmtMoney(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

export function fmtAge(age: number): string {
  if (age < 1) return `${Math.round(age * 24)}h`
  if (age < 2) return `${age.toFixed(1)}d`
  return `${Math.round(age)}d`
}

export function ageDisplay(age: number, isComplete: boolean): string {
  return isComplete ? '—' : fmtAge(age)
}

export function ageClassDisplay(age: number, isComplete: boolean): string {
  return isComplete ? '' : ageClass(age)
}

/** Latest non-deleted proof version (working or committed). */
export function latestActiveProof<T extends { deletedAt?: string }>(
  proofVersions: T[] | undefined
): T | null {
  if (!proofVersions?.length) return null
  const active = proofVersions.filter((p) => !p.deletedAt)
  return active.at(-1) ?? null
}

/** Reference helpers.ts — two-letter avatar initials. */
export function initials(name: string): string {
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase()
  }
  return (parts[0]?.slice(0, 2) ?? name.slice(0, 2)).toUpperCase()
}

export function shortName(name: string): string {
  if (!name) return ''
  if (name === 'RUKH System') return 'System'
  if (name === 'Client') return 'Client'
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length <= 1) return parts[0] ?? name
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`
}

/** Reference helpers.ts — H:MM:SS or MM:SS work-session display. */
export function fmtWorkTime(ms: number): string {
  const totalSecs = Math.floor(ms / 1000)
  const h = Math.floor(totalSecs / 3600)
  const m = Math.floor((totalSecs % 3600) / 60)
  const s = totalSecs % 60
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * STD-CUSTOM-QUEUE-001 / Origin helpers.ts lineHasPdWork —
 * custom, modify, or Standard-Custom (customFieldValues) lines have PD work.
 * Plain standard catalog ride-alongs do not.
 */
export function lineHasPdWork(line: {
  productType: 'custom' | 'standard'
  modify?: unknown
  customFieldValues?: unknown[]
}): boolean {
  return (
    line.productType === 'custom' ||
    !!line.modify ||
    (line.customFieldValues?.length ?? 0) > 0
  )
}

/** Reference helpers.ts stepCompleter — activity fallback when assignee missing. */
export function stepCompleter(
  stepKey: string,
  entry: { status: string; assignee: string | null } | undefined,
  activityHead: { e: string }[]
): string | null {
  if (!entry || entry.status !== 'complete') return null
  const assignee = entry.assignee
  if (assignee && assignee !== '__unassigned__') return assignee
  const words = stepKey.replace(/_/g, ' ')
  const hit = activityHead.find((x) => x.e.includes(`completed ${words}`))
  const match = hit?.e.match(/^(.*?) completed /)
  return match ? match[1] : null
}
